import React from 'react'

import Badge from '@totallymoney/ui/components/Badge'
import Box from '@totallymoney/ui/components/Box'
import Divider from '@totallymoney/ui/components/Divider'
import Heading from '@totallymoney/ui/components/Heading'

import {
    CreditCardProductCategory,
    OfferType,
    ScoredCreditCardProduct,
} from '@appTypes/EligibilityApi.interface'

import BannerOffer from '@components/BannerOffer'
import BannerStack from '@components/BannerStack'
import CardImage from '@components/CardImage'
import CardPositivesInfo from '@components/CardPositivesInfo'
import CreditCardContent from '@components/CreditCardContent'
import RepresentativeExample from '@components/RepresentativeExample'
import TmExclusiveIcon from '@components/TmExclusiveIcon/TmExclusiveIcon'

import { getCategoryBadgeText } from '@routes/ScoredTableRoute/components/ScoredTable/components/ScoredTableItem/utils/getCategoryBadgeText'
import { getProductInfoText } from '@routes/ScoredTableRoute/components/ScoredTable/components/ScoredTableItem/utils/getProductInfoText'
import { getShowPositivesList } from '@routes/ScoredTableRoute/components/ScoredTable/utils/getShowPositivesList'

import { getIsProductPortraitImage } from '@utils/getIsProductPortraitImage'

import * as Layout from './components/UnscoredTableItemLayout'

interface UnscoredTableItemProps {
    category: CreditCardProductCategory
    item: ScoredCreditCardProduct
    onShowRepresentativeExampleModal: () => void
    renderButtons: (item: ScoredCreditCardProduct) => React.ReactNode
}

const UnscoredTableItem = ({
    category,
    item,
    renderButtons,
    onShowRepresentativeExampleModal,
}: UnscoredTableItemProps) => {
    const categoryBadgeText =
        item.productCategories && category
            ? getCategoryBadgeText(item.productCategories, category)
            : ''

    const isExclusiveOffer = item.offerType === OfferType.TMExclusive
    const isMarketWideOffer = item.offerType === OfferType.MarketWide

    return (
        <>
            {isExclusiveOffer ? (
                <BannerStack>
                    <BannerOffer
                        prefix={<TmExclusiveIcon />}
                        title={item.offerHeading}
                        description={item.offerCopy}
                        backgroundColor="indigo30"
                        variant="backdrop"
                    />
                </BannerStack>
            ) : null}

            <Layout.ItemBase productCode={item.productCode}>
                <Layout.ItemWrapper>
                    <Layout.ItemHeading>
                        <Heading as="h2" variant="60">
                            {item.name}
                        </Heading>
                    </Layout.ItemHeading>

                    <Layout.ItemImage>
                        <Box width={[88, 139]}>
                            <CardImage
                                cmsImageUrl={item.imageUrlLarge}
                                name={item.name}
                                isPortraitImage={getIsProductPortraitImage(
                                    item.productCode
                                )}
                            />
                        </Box>
                    </Layout.ItemImage>

                    <Layout.ItemContent>
                        {categoryBadgeText ? (
                            <Box mb="space20">
                                <Badge
                                    text={categoryBadgeText}
                                    size="medium"
                                    icon="checkmark"
                                />
                            </Box>
                        ) : null}
                        <CreditCardContent
                            category={category}
                            creditCard={item}
                        />
                    </Layout.ItemContent>
                    <Layout.ItemButtons>
                        {renderButtons(item)}
                    </Layout.ItemButtons>
                </Layout.ItemWrapper>

                {getShowPositivesList({
                    category,
                    positives: item.bulletsPositive,
                }) ||
                isExclusiveOffer ||
                isMarketWideOffer ? (
                    <>
                        <Box mb="space20">
                            <Divider borderStyle="dashed" />
                        </Box>
                        <Box mb="space30">
                            <CardPositivesInfo
                                positives={[
                                    ...(item.bulletsPositive ?? []),
                                    getProductInfoText(
                                        item.offerHeading,
                                        item.offerCopy
                                    ),
                                ]}
                            />
                        </Box>
                    </>
                ) : null}

                <Layout.ItemFooter>
                    <RepresentativeExample
                        item={item}
                        onTooltipPress={onShowRepresentativeExampleModal}
                    />
                </Layout.ItemFooter>
            </Layout.ItemBase>
        </>
    )
}

export default UnscoredTableItem
